/**
 * Shared weighted rate calculation matching Old EA Report formula.
 * Used for display only (table/card labels, chips); does not affect contestable cost or other stored calculations.
 *
 * Formula (same as Old EA ProjectEAReportPreview): rate weighted by energy
 *   weightedPeak   = Σ(peakEnergy_i × peakRate_i) / Σ(peakEnergy_i)
 *   weightedOffPeak = Σ(offPeakEnergy_i × offPeakRate_i) / Σ(offPeakEnergy_i)
 *   weightedShoulder = Σ(shoulderEnergy_i × shoulderRate_i) / Σ(shoulderEnergy_i)
 *
 * Where weighted rate is used (display only, not used in cost/contestable calculations):
 * - EAReportNew/eaReportCalculations: weighedAverageData.peakRate/offPeakRate/shoulderRate → Current Contract display
 * - EAReportNew/CurrentVsNewSection: New Contract Bill Peak/Off Peak/Shoulder rate display
 * - EAReportNew/ContractTermComparisonSection: Peak/Off Peak/Shoulder table cells
 * - EAReportNew/SummarySection: Future table chips (Peak/Off Peak/Shoulder)
 * - EAReportNew/BestOfferSection: getCardRates for card display
 * - EAReportNew/aggregationHelpers: calculateRateUnitWeightedAvgFromSegments / FromMergedPeriods (merge display)
 * - OfferComparison/OfferCombinationDetailTable: Weighted Rate (c/kWh) column
 */

import { sumBy } from 'lodash-es';

export interface MeterLikeForWeightedRate {
  annualData?: {
    peakEnergy?: number;
    offPeakEnergy?: number;
    shoulderEnergy?: number;
  };
  contractPeriod?: {
    peakRate?: number;
    offPeakRate?: number;
    shoulderRate?: number;
  };
}

export interface SegmentLikeForWeightedRate {
  peak?: number;
  offPeak?: number;
  shoulder?: number;
  numOfDays?: number;
  /** Consumption (kWh) per band including loss – when present, used as weight instead of numOfDays × dailyX. */
  peakConsumptionIncLoss?: number;
  offPeakConsumptionIncLoss?: number;
  shoulderConsumptionIncLoss?: number;
}

export interface WeightedRatesResult {
  peak: number;
  offPeak: number;
  shoulder: number;
}

/**
 * Weighted rate from meters (Old EA formula).
 * Each meter contributes (energy × rate); result is Σ(energy × rate) / Σ(energy) per band.
 * Display only; not used for contestable cost calculation.
 */
export function calculateWeightedRatesByEnergyFromMeters(meters: MeterLikeForWeightedRate[]): WeightedRatesResult {
  const sumPeakEnergy = sumBy(meters, (v) => v.annualData?.peakEnergy ?? 0);
  const sumOffPeakEnergy = sumBy(meters, (v) => v.annualData?.offPeakEnergy ?? 0);
  const sumShoulderEnergy = sumBy(meters, (v) => v.annualData?.shoulderEnergy ?? 0);

  const peak =
    sumPeakEnergy > 0
      ? sumBy(meters, (v) => (v.annualData?.peakEnergy ?? 0) * (v.contractPeriod?.peakRate ?? 0)) / sumPeakEnergy
      : 0;
  const offPeak =
    sumOffPeakEnergy > 0
      ? sumBy(meters, (v) => (v.annualData?.offPeakEnergy ?? 0) * (v.contractPeriod?.offPeakRate ?? 0)) /
        sumOffPeakEnergy
      : 0;
  const shoulder =
    sumShoulderEnergy > 0
      ? sumBy(meters, (v) => (v.annualData?.shoulderEnergy ?? 0) * (v.contractPeriod?.shoulderRate ?? 0)) /
        sumShoulderEnergy
      : 0;

  return { peak, offPeak, shoulder };
}

/**
 * Weighted rate from segments using energy (consumption) weight.
 * Formula: weightedPeak = Σ(peakRate_i × peakConsumption_i) / Σ(peakConsumption_i), same for offPeak/shoulder.
 * When segments have peakConsumptionIncLoss (and offPeak/shoulder), those are used as weight; otherwise falls back to
 * numOfDays × dailyPeak (estimated energy). Display only.
 */
export function calculateWeightedRatesByEnergyFromSegments(
  segments: SegmentLikeForWeightedRate[],
  dailyPeak: number,
  dailyOffPeak: number,
  dailyShoulder: number
): WeightedRatesResult {
  if (segments.length === 0) {
    return { peak: 0, offPeak: 0, shoulder: 0 };
  }
  const last = segments[segments.length - 1];

  const peakWeight = (s: SegmentLikeForWeightedRate) => s.peakConsumptionIncLoss ?? (s.numOfDays ?? 0) * dailyPeak;
  const offPeakWeight = (s: SegmentLikeForWeightedRate) =>
    s.offPeakConsumptionIncLoss ?? (s.numOfDays ?? 0) * dailyOffPeak;
  const shoulderWeight = (s: SegmentLikeForWeightedRate) =>
    s.shoulderConsumptionIncLoss ?? (s.numOfDays ?? 0) * dailyShoulder;

  const denomPeak = sumBy(segments, peakWeight);
  const denomOff = sumBy(segments, offPeakWeight);
  const denomSh = sumBy(segments, shoulderWeight);

  const peak = denomPeak > 0 ? sumBy(segments, (s) => (s.peak ?? 0) * peakWeight(s)) / denomPeak : last?.peak ?? 0;
  const offPeak =
    denomOff > 0 ? sumBy(segments, (s) => (s.offPeak ?? 0) * offPeakWeight(s)) / denomOff : last?.offPeak ?? 0;
  const shoulder =
    denomSh > 0 ? sumBy(segments, (s) => (s.shoulder ?? 0) * shoulderWeight(s)) / denomSh : last?.shoulder ?? 0;

  return { peak, offPeak, shoulder };
}
